import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ViewChild,
  SimpleChanges,
  OnChanges,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DropDownSearchExtensionDirective } from '../../../kendo/DropDowns/dropdowns-search.ex-directive';
import { JobDropdown, ProfileModel } from '@ups/xplat/api/dto';
import { JobSearchService } from './job-search.service';
import { noop } from '@ups/xplat/utils';
import { JobSearchVPService } from './job-search-vp.service';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { BaseComponent } from '@ups/xplat/core';

export const JOB_SEARCH_CONTROL_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => JobSearchComponent),
  multi: true,
};

@Component({
  selector: 'ups-job-search',
  templateUrl: './job-search.component.html',
  styleUrls: ['./job-search.component.scss'],
  providers: [JobSearchVPService, JOB_SEARCH_CONTROL_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class JobSearchComponent
  extends BaseComponent
  implements ControlValueAccessor, OnInit, OnChanges
{
  @Output() valueChange: EventEmitter<JobDropdown> =
    new EventEmitter<JobDropdown>();
  @Output() valuesChange: EventEmitter<JobDropdown[]> = new EventEmitter<
    JobDropdown[]
  >();

  private readonly cdr = inject(ChangeDetectorRef);

  // Template-read state is signal-backed so writes that arrive outside this
  // component's own template events (writeValue from forms code, the async
  // default-job stream, a host ViewChild call, a host setting the property
  // directly) still repaint under OnPush. The public property names and
  // types are unchanged; they are accessors over the signals.
  private readonly selectedJobSig = signal<JobDropdown>(undefined);
  private readonly selectedJobsSig = signal<JobDropdown[]>(undefined);
  private readonly isMultiSelectSig = signal(false);
  private readonly includeClosedJobsSig = signal(false);

  get selectedJob(): JobDropdown {
    return this.selectedJobSig();
  }
  set selectedJob(v: JobDropdown) {
    this.selectedJobSig.set(v);
  }

  get selectedJobs(): JobDropdown[] {
    return this.selectedJobsSig();
  }
  set selectedJobs(v: JobDropdown[]) {
    this.selectedJobsSig.set(v);
  }

  @Input() disabled = false;

  /** Hosts also assign this directly (reports page restoring a saved filter), hence the accessor. */
  @Input()
  get includeClosedJobs(): boolean {
    return this.includeClosedJobsSig();
  }
  set includeClosedJobs(v: boolean) {
    this.includeClosedJobsSig.set(v);
  }
  @Input() showClosedJobsCheckbox = true;

  /** Hosts also assign this directly (safety/reports restoring a saved filter), hence the accessor. */
  @Input()
  get isMultiSelect(): boolean {
    return this.isMultiSelectSig();
  }
  set isMultiSelect(v: boolean) {
    this.isMultiSelectSig.set(v);
  }
  @Input() showMultiSelectCheckbox = false;

  @Input() showFavoritesButton = true;
  @Input() showFacilityTooltip = true;
  @Input() useDefaultJobFeature = true;
  /** When true, Sparta job dropdown merges temporary foreman jobs for this user (Foreman Allocation Sheet only). */
  @Input() includeTemporaryForemanJobs = false;

  @Input() placeholder = 'Search for Jobs...';

  @Input() companyIDs: number[] = [];
  @Input() parentFacilityIDs: number[] = [];
  @Input() hrRef: string;

  @Input() pointToViewpoint = false;

  @ViewChild(DropDownSearchExtensionDirective)
  searchEx: DropDownSearchExtensionDirective;

  isJobActive = false;
  profile: ProfileModel;

  filterDebounce: Subject<unknown> = new Subject<unknown>();

  touchedCallback: () => void = noop;
  changeCallback: (_: unknown) => void = noop;

  get value() {
    return this.selectedJob;
  }

  set value(v: JobDropdown) {
    if (v !== this.selectedJob) {
      this.selectedJob = v;
      this.changeCallback(v);
    }
  }

  constructor(
    private jobSearchService: JobSearchService,
    private jobSearchVPService: JobSearchVPService
  ) {
    super();
    this.profile = JSON.parse(localStorage.getItem('profile')) as ProfileModel;

    this.filterDebounce
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe((f) => this.filterChangeJob(f));
  }

  async ngOnInit() {
    if (this.useDefaultJobFeature) {
      await this.jobSearchService.getDefaultJob(
        this.profile.auth0UserId,
        this.hrRef || this.profile.hrRef
      );

      this.jobSearchService.defaultJob
        .pipe(takeUntil(this.destroy$))
        .subscribe((job) => {
          // Default API can emit null after unrelated updates; don't wipe a temp foreman job pick.
          if (job == null && this.selectedJobIsTemporaryForeman()) {
            return;
          }
          if (this.defaultJobStreamMatchesSelection(job)) {
            return;
          }
          this.valueChangeJob(job, false);
        });
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (
      changes.parentFacilityIDs &&
      !changes.parentFacilityIDs.firstChange &&
      this.searchEx
    ) {
      this.searchEx.clearDropdownItems();
    }

    if (
      this.useDefaultJobFeature &&
      changes.hrRef &&
      !changes.hrRef.firstChange
    ) {
      await this.jobSearchService.getDefaultJob(
        this.profile.auth0UserId,
        this.hrRef
      );
    }
  }

  onBlur() {
    this.touchedCallback();
  }

  writeValue(value: JobDropdown) {
    if (value !== this.selectedJob) {
      this.selectedJob = value;
      this.selectedJobs = [value];
    }
  }

  registerOnChange(fn: (_: unknown) => void) {
    this.changeCallback = fn;
  }

  registerOnTouched(fn: () => void) {
    this.touchedCallback = fn;
  }

  /** Avoid duplicate parent notifications when `defaultJob` already matches the current pick (e.g. optimistic publish). */
  private defaultJobStreamMatchesSelection(job: JobDropdown | null): boolean {
    if (
      job == null &&
      (this.selectedJob == null || this.selectedJob === undefined)
    ) {
      return true;
    }
    if (job == null || this.selectedJob == null) {
      return false;
    }
    return String(this.selectedJob.Id) === String(job.Id);
  }

  private selectedJobIsTemporaryForeman(): boolean {
    const j = this.selectedJob as JobDropdown & {
      isTemporaryForemanJob?: boolean;
      IsTemporaryForemanJob?: boolean;
    };
    return (
      j?.isTemporaryForemanJob === true || j?.IsTemporaryForemanJob === true
    );
  }

  clear() {
    this.selectedJob = null;
    this.selectedJobs = [];
    this.searchEx.clearDropdownItems();
    // clearDropdownItems writes the Kendo list's data outside our template.
    this.cdr.markForCheck();
  }

  /**
   * Ensures the dropdown lists the job and shows it as selected (e.g. after creating a temporary foreman job).
   */
  async ensureJobVisibleAndSelect(job: JobDropdown | null): Promise<void> {
    if (!job || this.pointToViewpoint) {
      return;
    }
    this.writeValue(job);
    const service = this.jobSearchService;
    const query =
      job.JobID || (job.Name ? String(job.Name).slice(0, 24) : '') || '';
    try {
      const data = (await service.searchJobs(
        query,
        this.companyIDs,
        this.hrRef,
        this.includeClosedJobs ? null : true,
        this.includeTemporaryForemanJobs,
        this.parentFacilityIDs
      )) as JobDropdown[];
      const list = Array.isArray(data) ? [...data] : [];
      const same = (a: JobDropdown, b: JobDropdown) =>
        a.JobID === b.JobID && String(a.Id) === String(b.Id);
      if (!list.some((j) => same(j, job))) {
        list.unshift(job);
      }
      setTimeout(() => {
        this.searchEx?.applyData(list);
        this.cdr.markForCheck();
      }, 0);
    } catch {
      setTimeout(() => {
        this.searchEx?.applyData([job]);
        this.cdr.markForCheck();
      }, 0);
    }
  }

  filterChangeJob($event) {
    const active = this.includeClosedJobs ? null : true;
    const promise = this.pointToViewpoint
      ? this.jobSearchVPService.searchJobs(
          $event.query,
          this.companyIDs,
          this.hrRef,
          active
        )
      : this.jobSearchService.searchJobs(
          $event.query,
          this.companyIDs,
          this.hrRef,
          active,
          this.includeTemporaryForemanJobs,
          this.parentFacilityIDs
        );
    promise
      .then((data) => {
        $event.extension.applyData(data);
        // applyData writes the Kendo list's data/loading outside our template.
        this.cdr.markForCheck();
      })
      .catch((error) => {
        $event.extension.applyData([]);
        this.cdr.markForCheck();
        throw error;
      });
  }

  valueChangeJobs(jobs: JobDropdown[]) {
    this.selectedJobs = jobs;
    if (jobs && jobs.length) {
      this.valueChangeJob(jobs[0]);
    } else {
      this.valueChangeJob(null);
    }
  }

  async valueChangeJob(job: JobDropdown, saveDefault: boolean = true) {
    this.selectedJob = job;

    if (!this.isMultiSelect) {
      if (job) {
        this.selectedJobs = [job];
      } else {
        this.selectedJobs = [];
      }
    }

    const j = job as JobDropdown & {
      isTemporaryForemanJob?: boolean;
      IsTemporaryForemanJob?: boolean;
    };
    const skipDefault =
      j?.isTemporaryForemanJob === true || j?.IsTemporaryForemanJob === true;
    const shouldPersist =
      this.useDefaultJobFeature && saveDefault && job && !skipDefault;

    this.valueChange.emit(job);
    this.valuesChange.emit(this.selectedJobs);
    this.isJobActive = job && job.IsActive ? true : false;

    if (shouldPersist) {
      await this.jobSearchService.saveDefaultJob(job.JobID, job);
    }
  }

  onMultiSelectCheckboxChange() {
    if (!this.isMultiSelect) {
      this.valueChangeJob(this.selectedJob);
    }
  }

  toggleFavorite(job: JobDropdown, e: PointerEvent) {
    job.IsUserFavorite = !job.IsUserFavorite;
    if (this.pointToViewpoint) {
      this.jobSearchVPService.addOrRemoveFavoriteJob(job.JobID);
    } else {
      this.jobSearchService.addOrRemoveFavoriteJob(job.Id);
    }
    e.stopPropagation();
  }
}
